/**
 * ChatPanel 组件
 * 统一的聊天面板组件，支持 AgentFlow 和 MemoryService 两种风格
 * 支持流式响应、打字机效果、执行路径显示等功能
 */

import { useState, useRef, useEffect, memo, useCallback } from 'react'
import { Bot, MessageSquare, RefreshCw, X, Zap, GitBranch, LogOut, ChevronRight, Loader2, AlertTriangle, Lock, RotateCcw, CheckCircle2, Sparkles, Wrench } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import type { ChatMessage as ChatMessageType, ExecutionNode, RouteInfo, ChatVariant, ChatTheme, ExtractionBanner, SessionInfo, AudioFormat } from './types'

interface ChatPanelProps {
  // 消息列表
  messages: ChatMessageType[]
  
  // 加载状态
  isLoading?: boolean
  
  // 流式内容（用于打字机效果）
  streamingContent?: string
  
  // 执行路径（AgentFlow 特有）
  executionPath?: ExecutionNode[]
  currentNode?: string
  routeInfo?: RouteInfo
  
  // 样式配置
  variant?: ChatVariant
  theme?: ChatTheme
  
  // 头部配置
  title?: string
  subtitle?: string
  showHeader?: boolean
  
  // 回调函数
  onSend?: (message: string) => void
  onClear?: () => void
  onClose?: () => void
  
  // 自定义渲染
  renderHeader?: () => React.ReactNode
  renderFooter?: () => React.ReactNode
  
  // 其他配置
  placeholder?: string
  emptyStateTitle?: string
  emptyStateDescription?: string
  emptyStateSuggestions?: string[]
  autoFocus?: boolean
  
  // MemoryService 特有功能
  extractionBanner?: ExtractionBanner
  sessionInfo?: SessionInfo
  readOnly?: boolean
  readOnlyMessage?: string
  
  // v6.0: 语音输入支持
  showVoiceButton?: boolean
  voiceMode?: 'off' | 'ptt' | 'vad' | 'vad_interrupt'
  isRecording?: boolean
  onVoiceClick?: () => void
  onVoiceLongPressStart?: (e: React.MouseEvent | React.TouchEvent) => void
  onVoiceLongPressEnd?: () => void
  // v6.0: 语音播放支持
  isAudioPlaying?: boolean
  onPlayAudio?: (audioData: string, format: AudioFormat) => void
  
  // v8.2: 布局选项
  fullWidth?: boolean  // 是否全宽显示（用于 Sheet 内等场景）
  
  // v11.3: Structured Agent 可观测性
  agentStatus?: {
    type: 'thinking' | 'tool'  // thinking=推理中, tool=工具调用中
    text: string               // 显示文本
  } | null
  
  // v13.0: 流式推理内容（reasoning 事件累积，完成后写入消息）
  streamingReasoning?: string
}

// 打字机效果组件
const TypewriterText = memo(({ text, speed = 30 }: { text: string; speed?: number }) => {
  const [displayedText, setDisplayedText] = useState('')
  const indexRef = useRef(0)

  useEffect(() => {
    if (text.length > indexRef.current) {
      const timer = setInterval(() => {
        if (indexRef.current < text.length) {
          indexRef.current += 1
          setDisplayedText(text.slice(0, indexRef.current))
        } else {
          clearInterval(timer)
        }
      }, speed)
      return () => clearInterval(timer)
    } else {
      setDisplayedText(text)
    }
  }, [text, speed])

  return (
    <>
      {displayedText}
      {indexRef.current < text.length && (
        <span className="inline-block w-[2px] h-[14px] bg-violet-500 animate-pulse ml-[2px] align-middle" />
      )}
    </>
  )
})
TypewriterText.displayName = 'TypewriterText'

const ChatPanelComponent = ({
  messages,
  isLoading = false,
  streamingContent = '',
  executionPath = [],
  currentNode = '',
  routeInfo,
  variant = 'agent',
  theme = 'light',
  title,
  subtitle,
  showHeader = true,
  onSend,
  onClear,
  onClose,
  renderHeader,
  renderFooter,
  placeholder,
  emptyStateTitle,
  emptyStateDescription,
  emptyStateSuggestions,
  autoFocus = false,
  extractionBanner,
  sessionInfo,
  readOnly = false,
  readOnlyMessage = '查看他人会话（只读模式）',
  // v6.0: 语音输入
  showVoiceButton = false,
  voiceMode = 'off',
  isRecording = false,
  onVoiceClick,
  onVoiceLongPressStart,
  onVoiceLongPressEnd,
  // v6.0: 语音播放
  isAudioPlaying = false,
  onPlayAudio,
  // v8.2: 布局选项
  fullWidth = false,
  // v11.3: Structured Agent 可观测性
  agentStatus,
  // v13.0: 流式推理内容
  streamingReasoning,
}: ChatPanelProps) => {
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const displayIndexRef = useRef(0)
  const prevStreamingContentRef = useRef('')
  const typewriterTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [displayedStreamContent, setDisplayedStreamContent] = useState('')

  // 深色主题支持：variant === 'memory' 或 theme === 'dark' 时使用深色样式

  // 调试日志
  console.log('🔍 [ChatPanel] 渲染 - props:', JSON.stringify({
    messagesCount: messages.length,
    variant,
    theme,
    isLoading,
    readOnly,
  }, null, 2))
  
  // 记录之前的 isLoading 状态，用于在加载完成后恢复焦点
  const wasLoadingRef = useRef(false)

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, displayedStreamContent])

  // 当加载完成后（isLoading 从 true 变为 false），自动重新聚焦输入框
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && !readOnly) {
      // 延迟一小段时间确保 input 已经 enabled
      setTimeout(() => {
        inputRef.current?.focus()
      }, 50)
    }
    wasLoadingRef.current = isLoading
  }, [isLoading, readOnly])

  // 🔒 稳定的打字机控制函数，不依赖任何外部状态
  const clearTypewriter = useCallback(() => {
    if (typewriterTimerRef.current) {
      clearInterval(typewriterTimerRef.current)
      typewriterTimerRef.current = null
    }
  }, [])

  const startTypewriter = useCallback((content: string) => {
    clearTypewriter()
    
    typewriterTimerRef.current = setInterval(() => {
      if (displayIndexRef.current < content.length) {
        displayIndexRef.current += 1
        setDisplayedStreamContent(content.slice(0, displayIndexRef.current))
      } else {
        clearTypewriter()
      }
    }, 5) // 每5ms显示一个字符，快速完成避免被副分支干扰
  }, [clearTypewriter])

  // 🔒 打字机效果：完全独立的状态管理，不受外部 props 变化影响
  useEffect(() => {
    console.log('🎯 [ChatPanel] 打字机effect触发，streamingContent长度:', streamingContent?.length || 0)
    
    clearTypewriter()

    if (streamingContent) {
      const prevContent = prevStreamingContentRef.current
      
      // 🔧 检查新内容是否是之前内容的扩展
      const isContentExtension = streamingContent.startsWith(prevContent)
      
      if (!isContentExtension) {
        // 全新的内容，重置打字机
        console.log('🔄 [ChatPanel] 全新内容，重置打字机')
        displayIndexRef.current = 0
        setDisplayedStreamContent('')
      }
      
      // 更新内容引用
      prevStreamingContentRef.current = streamingContent
      
      // 🚀 启动打字机，使用稳定的函数引用
      startTypewriter(streamingContent)
    } else {
      // 清空所有状态
      console.log('🧹 [ChatPanel] 清空打字机状态')
      setDisplayedStreamContent('')
      displayIndexRef.current = 0
      prevStreamingContentRef.current = ''
    }

    // 清理函数
    return clearTypewriter
  }, [streamingContent, startTypewriter, clearTypewriter])

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (typewriterTimerRef.current) {
        clearInterval(typewriterTimerRef.current)
        typewriterTimerRef.current = null
      }
    }
  }, [])

  const handleSend = () => {
    if (!input.trim() || isLoading || !onSend) return
    onSend(input.trim())
    setInput('')
    
    // 🔧 修复：清理打字机状态时也要清理定时器和引用
    if (typewriterTimerRef.current) {
      clearInterval(typewriterTimerRef.current)
      typewriterTimerRef.current = null
    }
    displayIndexRef.current = 0
    setDisplayedStreamContent('')
    prevStreamingContentRef.current = ''
    
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion)
    inputRef.current?.focus()
  }

  // AgentFlow 风格（浅色）
  if (variant === 'agent' && theme === 'light') {
    return (
      <aside className={`${fullWidth ? 'w-full' : 'w-[400px]'} ${fullWidth ? '' : 'border-l'} flex flex-col shrink-0 bg-[#f7f7f8] h-full`}>
        {/* 头部 */}
        {showHeader && (
          <div className="px-4 py-3 border-b bg-white">
            {renderHeader ? (
              renderHeader()
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <MessageSquare className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-medium text-sm text-slate-800">
                      {title || '测试对话'}
                    </h3>
                    {subtitle && (
                      <p className="text-[10px] text-slate-400">{subtitle}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {onClear && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-slate-500 hover:text-slate-700 text-xs"
                      onClick={onClear}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      清空
                    </Button>
                  )}
                  {onClose && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-500 hover:text-slate-700"
                      onClick={onClose}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
            
            {/* 执行路径显示 */}
            {(isLoading || executionPath.length > 0) && (
              <div className="mt-3 p-2 bg-slate-50 rounded-lg">
                <div className="text-[10px] text-slate-500 mb-1.5 font-medium">执行路径</div>
                <div className="flex items-center gap-1 flex-wrap">
                  {executionPath.map((node, index) => (
                    <div key={index} className="flex items-center">
                      <div
                        className={`px-2 py-1 rounded text-[10px] flex flex-col ${
                          node.status === 'running'
                            ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300'
                            : node.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : currentNode === node.node
                            ? 'bg-violet-100 text-violet-700 ring-1 ring-violet-300'
                            : 'bg-white text-slate-600 border border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          {node.node === 'entry' && <Zap className="h-2.5 w-2.5" />}
                          {node.node?.startsWith('router') && <GitBranch className="h-2.5 w-2.5" />}
                          {node.node?.startsWith('agent') && <Bot className="h-2.5 w-2.5" />}
                          {node.node === 'output' && <LogOut className="h-2.5 w-2.5" />}
                          <span>{node.name || node.node || '未知节点'}</span>
                          {/* 根据 status 显示不同图标 */}
                          {node.status === 'running' && (
                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          )}
                          {node.status === 'completed' && (
                            <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" />
                          )}
                          {/* 兼容旧逻辑：没有 status 时用 currentNode + isLoading 判断 */}
                          {!node.status && currentNode === node.node && isLoading && (
                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          )}
                        </div>
                        {node.model && (
                          <div className="text-[8px] text-slate-400 mt-0.5 truncate max-w-[80px]" title={node.model}>
                            {node.model}
                          </div>
                        )}
                      </div>
                      {index < executionPath.length - 1 && (
                        <ChevronRight className="h-3 w-3 text-slate-300 mx-0.5" />
                      )}
                    </div>
                  ))}
                </div>
                
                {/* 路由结果 */}
                {routeInfo && (
                  <div className="mt-2 text-[10px] bg-amber-50 px-2 py-1.5 rounded space-y-1">
                    <div className="flex items-center gap-1 text-amber-700">
                      <GitBranch className="h-3 w-3" />
                      <span>匹配条件「{routeInfo.condition}」→ {routeInfo.target}</span>
                    </div>
                    {routeInfo.reason && (
                      <div className="text-amber-600/80 pl-4">
                        💡 {routeInfo.reason}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* 消息列表 */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-6">
            {/* 空状态 */}
            {messages.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                  <Bot className="h-8 w-8 text-violet-600" />
                </div>
                <h4 className="font-medium text-slate-700 mb-2">
                  {emptyStateTitle || '开始测试'}
                </h4>
                <p className="text-sm text-slate-400 mb-6">
                  {emptyStateDescription || '发送消息测试 Agent 流程'}
                </p>
                {emptyStateSuggestions && emptyStateSuggestions.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-2">
                    {emptyStateSuggestions.map((text) => (
                      <button
                        key={text}
                        onClick={() => handleSuggestionClick(text)}
                        className="px-4 py-2 text-sm bg-white rounded-full text-slate-600 hover:bg-violet-50 hover:text-violet-700 border border-slate-200 hover:border-violet-200 transition-all shadow-sm"
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* 消息列表 */}
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                variant={variant}
                theme={theme}
                isAudioPlaying={isAudioPlaying}
                onPlayAudio={onPlayAudio}
              />
            ))}
            
            {/* v13.0: 推理 + 工具状态 + 最终回复，统一在一个 Bot 头像下 */}
            {isLoading && (streamingReasoning || displayedStreamContent || agentStatus) && (
              <div className="flex gap-3">
                <div className="shrink-0">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="flex-1 max-w-[85%] space-y-2">
                  {/* 推理过程（灰色区域，流式出现） */}
                  {streamingReasoning && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="h-3 w-3 text-slate-400 animate-pulse" />
                        <span className="text-xs font-medium text-slate-500">推理过程</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">{streamingReasoning}</p>
                    </div>
                  )}
                  {/* 工具/思考状态（内联，无独立 Bot 头像） */}
                  {agentStatus && !displayedStreamContent && (
                    <div className="flex items-center gap-2 py-0.5">
                      {agentStatus.type === 'thinking' ? (
                        <Sparkles className="h-3 w-3 text-teal-400 animate-pulse" />
                      ) : (
                        <Wrench className="h-3 w-3 text-amber-400 animate-spin" style={{ animationDuration: '2s' }} />
                      )}
                      <span className="text-xs text-slate-400 animate-pulse">{agentStatus.text}</span>
                    </div>
                  )}
                  {/* 最终回复（正常气泡，推理后出现） */}
                  {displayedStreamContent && (
                    <div className="bg-white px-4 py-3 rounded-2xl rounded-tl-md shadow-sm border border-slate-100">
                      <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                        <TypewriterText text={displayedStreamContent} />
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 等待中 - 加载动画（无任何内容时显示三点跳动） */}
            {isLoading && !displayedStreamContent && !streamingReasoning && !agentStatus && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex gap-3">
                <div className="shrink-0">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="bg-white px-4 py-4 rounded-2xl rounded-tl-md shadow-sm border border-slate-100 inline-block">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.6s' }} />
                      <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s', animationDuration: '0.6s' }} />
                      <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s', animationDuration: '0.6s' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* 输入区域 */}
        <ChatInput
          ref={inputRef}
          value={input}
          onChange={setInput}
          onSend={handleSend}
          placeholder={placeholder || '输入消息...'}
          disabled={isLoading}
          isLoading={isLoading}
          variant={variant}
          theme={theme}
          autoFocus={autoFocus}
          // v6.0: 语音输入
          showVoiceButton={showVoiceButton}
          voiceMode={voiceMode}
          isRecording={isRecording}
          onVoiceClick={onVoiceClick}
          onVoiceLongPressStart={onVoiceLongPressStart}
          onVoiceLongPressEnd={onVoiceLongPressEnd}
        />

        {/* 自定义底部 */}
        {renderFooter && renderFooter()}
      </aside>
    )
  }

  // MemoryService 风格（深色）
  return (
    <div className="flex-1 flex flex-col h-full bg-background">
      {/* Header */}
      {showHeader && (
        <header className="px-6 py-4 flex items-center justify-between border-b border-border">
          {renderHeader ? (
            renderHeader()
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold bg-gradient-to-br from-blue-500 to-purple-600">
                AI
              </div>
              <div>
                <h2 className="font-semibold flex items-center gap-2 text-foreground">
                  {title || 'Memory Chat'}
                  {sessionInfo?.sessionId && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      !sessionInfo.isViewingOthers
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {!sessionInfo.isViewingOthers ? '我的会话' : '查看他人'}
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {sessionInfo?.userId && (
                    <span className="text-xs px-2 py-0.5 rounded flex items-center gap-1 bg-blue-500/15 text-blue-400">
                      <span>👤</span>
                      <span className="font-medium">{sessionInfo.userId}</span>
                    </span>
                  )}
                  {sessionInfo?.sessionId && (
                    <>
                      <span className="text-xs text-muted-foreground">|</span>
                      <span className="text-xs text-muted-foreground">
                       会话: <span className="text-blue-400">{sessionInfo.sessionId.substring(0, 8)}</span>
                      </span>
                    </>
                  )}
                  {sessionInfo?.messageCount !== undefined && (
                    <>
                      <span className="text-xs text-muted-foreground">|</span>
                      <span className="text-xs text-muted-foreground">
                        消息: <span className="text-foreground">{sessionInfo.messageCount}</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </header>
      )}

      {/* 提取失败/延时提示横幅 */}
      {extractionBanner && (
        <div className={`mx-4 mt-2 px-4 py-3 rounded-xl flex items-center justify-between ${
          extractionBanner.status === 'failed' 
            ? 'bg-red-500/15 border border-red-500/30' 
            : 'bg-yellow-500/15 border border-yellow-500/30'
        }`}>
          <div className="flex items-center gap-3">
            <AlertTriangle
              className={`w-5 h-5 flex-shrink-0 ${
                extractionBanner.status === 'failed' ? 'text-red-400' : 'text-yellow-400'
              }`}
            />
            <div>
              <div className={`text-sm font-medium ${
                extractionBanner.status === 'failed' ? 'text-red-400' : 'text-yellow-400'
              }`}>
                {extractionBanner.message}
              </div>
              {extractionBanner.detail && (
                <div className="text-xs text-muted-foreground">
                  {extractionBanner.detail}
                </div>
              )}
            </div>
          </div>
          {extractionBanner.onRetry && (
            <button
              onClick={extractionBanner.onRetry}
              disabled={extractionBanner.isRetrying}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50 text-white ${
                extractionBanner.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
              }`}
            >
              {extractionBanner.isRetrying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              <span>重新提取</span>
            </button>
          )}
        </div>
      )}

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {messages.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="text-6xl mb-4">💬</div>
            <h3 className="text-xl font-semibold mb-2 text-foreground">
              {emptyStateTitle || '开始对话'}
            </h3>
            <p className="text-muted-foreground">
              {emptyStateDescription || '发送消息开始新会话，AI 将自动提取记忆'}
            </p>
          </div>
        ) : (
          messages.map((message) => {
            const isLastMessage = message.id === messages[messages.length - 1]?.id
            const isStreaming = isLoading && isLastMessage && message.role === 'assistant'
            return (
              <ChatMessage
                key={message.id}
                message={message}
                isStreaming={isStreaming}
                variant={variant}
                theme={theme}
                isAudioPlaying={isAudioPlaying}
                onPlayAudio={onPlayAudio}
              />
            )
          })
        )}

        {/* 加载指示器 */}
        {isLoading && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
          <div className="flex justify-start animate-slide-up">
            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-secondary border border-border">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full animate-bounce bg-muted-foreground" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full animate-bounce bg-muted-foreground" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full animate-bounce bg-muted-foreground" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 输入区域 */}
      {readOnly ? (
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-center gap-2 p-4 rounded-2xl bg-card border border-border">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {readOnlyMessage}
            </span>
          </div>
        </div>
      ) : onSend ? (
        <ChatInput
          ref={inputRef}
          value={input}
          onChange={setInput}
          onSend={handleSend}
          placeholder={placeholder || '输入消息，按 Enter 发送...'}
          disabled={isLoading}
          isLoading={isLoading}
          variant={variant}
          theme={theme}
          autoFocus={autoFocus}
        />
      ) : null}
    </div>
  )
}

// 🔧 自定义比较函数：严格控制重新渲染，保护打字机效果
const arePropsEqual = (prevProps: ChatPanelProps, nextProps: ChatPanelProps) => {
  // v13.0: 推理内容变化时必须重新渲染
  if (prevProps.streamingReasoning !== nextProps.streamingReasoning) {
    return false
  }

  // 🚀 打字机核心状态：streamingContent 变化时必须重新渲染
  if (prevProps.streamingContent !== nextProps.streamingContent) {
    console.log(`🔄 [ChatPanel] streamingContent 变化，允许重新渲染`)
    return false
  }
  
  // 🚀 加载状态：isLoading 变化时必须重新渲染
  if (prevProps.isLoading !== nextProps.isLoading) {
    console.log(`🔄 [ChatPanel] isLoading 变化: ${prevProps.isLoading} -> ${nextProps.isLoading}`)
    return false
  }
  
  // 🚀 消息变化：只检查数量变化，避免内容比较的性能开销
  if (prevProps.messages.length !== nextProps.messages.length) {
    console.log(`🔄 [ChatPanel] 消息数量变化: ${prevProps.messages.length} -> ${nextProps.messages.length}`)
    return false
  }
  
  // 🚫 严格忽略 Flow 相关的所有状态变化
  // 这些状态变化只应该影响 ReactFlow 组件，不应该影响 ChatPanel
  const flowRelatedProps = ['executionPath', 'currentNode', 'routeInfo'] as const
  
  // 🔧 只允许真正影响 ChatPanel UI 的 props 触发重新渲染
  const chatUIProps = ['variant', 'theme'] as const
  for (const prop of chatUIProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      console.log(`🔄 [ChatPanel] UI配置变化: ${prop}`)
      return false
    }
  }

  // 🔧 renderHeader/renderFooter 变化时必须重新渲染（内部有动态状态）
  if (prevProps.renderHeader !== nextProps.renderHeader) {
    return false
  }
  if (prevProps.renderFooter !== nextProps.renderFooter) {
    return false
  }
  
  // 🚫 检测并忽略 Flow 状态变化
  let hasFlowChanges = false
  for (const prop of flowRelatedProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      console.log(`🚫 [ChatPanel] 忽略Flow状态变化: ${prop} (保护打字机效果)`)
      hasFlowChanges = true
    }
  }
  
  if (hasFlowChanges) {
    console.log(`✅ [ChatPanel] Flow状态变化被忽略，打字机效果不中断`)
  }
  
  // 🔒 默认不重新渲染，严格保护打字机效果
  return true
}

export const ChatPanel = memo(ChatPanelComponent, arePropsEqual)

ChatPanel.displayName = 'ChatPanel'

